import { useEffect, useState } from 'react';
import { Plus, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import {
  adminList, adminCreate, adminUpdate, adminDelete, adminReorder,
} from '../../../lib/adminApi';
import { SectionHeader, SaveBar, useSaver, ConfirmDelete, Field, Card } from './_ui';

export default function FaqsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => { setItems(await adminList('faqs')); setLoading(false); };
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    await adminCreate('faqs', {
      q: 'New question?', a: '', published: true, order: items.length,
    });
    await refresh();
  };
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await adminReorder('faqs', next.map((x) => x.id));
  };

  return (
    <div className="max-w-4xl">
      <SectionHeader
        title="FAQs"
        description="Frequently asked questions displayed on the site."
        actions={
          <Button onClick={add} className="bg-brand-sun text-brand-ink hover:brightness-95 rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Add FAQ
          </Button>
        }
      />
      {loading ? <p className="text-brand-ink/60">Loading...</p> : (
        <div className="space-y-4">
          {items.map((it, i) => (
            <FaqRow
              key={it.id}
              faq={it}
              index={i}
              total={items.length}
              onChange={(next) => setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, ...next } : x)))}
              onSave={async (next) => { await adminUpdate('faqs', it.id, next); }}
              onDelete={async () => { await adminDelete('faqs', it.id); await refresh(); }}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FaqRow({ faq, index, total, onChange, onSave, onDelete, onMoveUp, onMoveDown }) {
  const saver = useSaver();
  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="flex flex-col shrink-0">
          <button type="button" onClick={onMoveUp} disabled={index === 0}
                  className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
          <button type="button" onClick={onMoveDown} disabled={index === total - 1}
                  className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 min-w-0 space-y-3">
          <Field label="Question">
            <Input value={faq.q || ''} onChange={(e) => onChange({ q: e.target.value })} />
          </Field>
          <Field label="Answer">
            <Textarea rows={3} value={faq.a || ''} onChange={(e) => onChange({ a: e.target.value })} />
          </Field>
          <div className="flex items-center gap-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <Switch checked={faq.published !== false} onCheckedChange={(v) => onChange({ published: v })} />
              Published
            </label>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-brand-ink/10">
            <ConfirmDelete onConfirm={onDelete} />
            <SaveBar {...saver} onSave={() => saver.run(() => onSave(faq))} dataTestId={`save-faq-${index}`} />
          </div>
        </div>
      </div>
    </Card>
  );
}
